import { Component, OnInit } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { SettingsService } from '../settings.service';
import { LoginService } from '../login.service';


const CHOICE_TYPES = ['select', 'radio', 'checkbox'];
const CONTENT_BLOCK_TYPES = ['opening', 'closing'];
// AR-32: a repeatableGroup's nested subQuestions may only be the "ordinary
// answer" types - no nesting-in-nesting, no content blocks (nothing to
// repeat), no signature (one signature per form). Mirrors the backend's
// SUB_QUESTION_TYPES in schema/questionnaires.js.
const GROUP_TYPE = 'repeatableGroup';
const SUB_QUESTION_TYPES = ['text', 'select', 'radio', 'checkbox', 'date', 'number', 'boolean'];

@Component({
  selector: 'app-questionnaire-templates',
  templateUrl: './questionnaire-templates.component.html',
  styleUrls: ['./questionnaire-templates.component.scss'],
  standalone: false
})
export class QuestionnaireTemplatesComponent implements OnInit {
  questionnaires: any[] = [];
  defaults: any[] = [];  // company-provided templates the doctor can copy
  showDefaults = false;  // collapsed by default; expand on clicking the section title
  exams: any[] = [];
  editing: any = null;   // the questionnaire currently being created/edited (null = list view)
  error = false;

  // Exam-type uniqueness conflict popup (item 7): exams already owned by another
  // questionnaire, surfaced when the backend rejects a save with 409.
  examConflicts: any[] = [];
  showConflictModal = false;

  // value = stored in DB / questionType; label = i18n key, rendered via `| translate` in the template
  questionTypes = [
    { value: 'text', label: 'appointments.gui.questionnaires.types.Free Text' },
    { value: 'select', label: 'appointments.gui.questionnaires.types.Single Choice Dropdown' },
    { value: 'radio', label: 'appointments.gui.questionnaires.types.Single Choice Radio' },
    { value: 'checkbox', label: 'appointments.gui.questionnaires.types.Multiple Choice' },
    { value: 'date', label: 'appointments.gui.questionnaires.types.Date' },
    { value: 'number', label: 'appointments.gui.questionnaires.types.Number' },
    { value: 'boolean', label: 'appointments.gui.questionnaires.types.Yes No' },
    { value: 'signature', label: 'appointments.gui.questionnaires.types.Signature' },
    { value: 'opening', label: 'appointments.gui.questionnaires.types.Opening block' },
    { value: 'closing', label: 'appointments.gui.questionnaires.types.Closing block' },
    { value: GROUP_TYPE, label: 'appointments.gui.questionnaires.types.Repeatable group' },
  ];

  // Same list, minus repeatableGroup itself, for a sub-question's own type picker.
  subQuestionTypes = this.questionTypes.filter((t) => SUB_QUESTION_TYPES.includes(t.value));

  // Tokens auto-filled per patient when the questionnaire is rendered (patient side, future).
  placeholders = [
    { token: '{{patientName}}', label: 'appointments.gui.questionnaires.placeholders.Patient name' },
    { token: '{{patientId}}', label: 'appointments.gui.questionnaires.placeholders.Patient ID' },
    { token: '{{date}}', label: 'appointments.gui.questionnaires.placeholders.Date' },
    { token: '{{doctorName}}', label: 'appointments.gui.questionnaires.placeholders.Doctor name' },
    { token: '{{clinicName}}', label: 'appointments.gui.questionnaires.placeholders.Clinic name' },
    { token: '{{examName}}', label: 'appointments.gui.questionnaires.placeholders.Exam name' },
  ];

  prefillFields = [
    { value: '', label: 'appointments.gui.questionnaires.prefill.None' },
    { value: 'patientName', label: 'appointments.gui.questionnaires.prefill.Patient name' },
    { value: 'patientId', label: 'appointments.gui.questionnaires.prefill.Patient ID' },
    { value: 'birthDate', label: 'appointments.gui.questionnaires.prefill.Birth date' },
    { value: 'lmp', label: 'appointments.gui.questionnaires.prefill.LMP' },
    { value: 'examName', label: 'appointments.gui.questionnaires.prefill.Exam name' },
    { value: 'phone', label: 'appointments.gui.questionnaires.prefill.Phone' },
  ];

  private PREFILLABLE_TYPES = ['text', 'date', 'number', 'select', 'radio'];

  // Extra input shown to the patient when she picks an option that carries one
  // (e.g. "Other"). '' = none.
  freeTextTypes = [
    { value: '', label: 'appointments.gui.questionnaires.freeTextType.None' },
    { value: 'text', label: 'appointments.gui.questionnaires.freeTextType.Free text' },
    { value: 'number', label: 'appointments.gui.questionnaires.freeTextType.Number only' },
  ];

  constructor(public settingsService: SettingsService, public ls: LoginService) { }

  async ngOnInit() {
    // Real examsSettings rows, or specialty defaults (AutoIndex null) for a
    // questionnaire-only clinic with no examsSettings (item 6).
    this.exams = await this.settingsService.getQuestionnaireExamOptions();
    await this.loadQuestionnaires();
    this.defaults = await this.settingsService.getQuestionnaireDefaults();
  }

  async loadQuestionnaires() {
    this.questionnaires = await this.settingsService.getQuestionnaires();
  }

  // Stable key for an exam option: real rows key by AutoIndex, fallback name-only
  // options key by 'name:<examName>' (item 6). Used to drive the multi-select
  // and to split the selection back into examIds + examNames on save.
  examKey(e: any): string {
    return e && e.AutoIndex != null ? String(e.AutoIndex) : 'name:' + (e ? e.examName : '');
  }

  trackByIndex(index: number): number {
    return index;
  }

  // Auto-grow textareas (question/option text) so long content wraps and stays
  // visible instead of scrolling out of a fixed-height single-line box.
  autoGrow(event: Event) {
    const el = event.target as HTMLTextAreaElement;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }

  isChoiceType(type: string): boolean {
    return CHOICE_TYPES.includes(type);
  }

  isContentBlock(type: string): boolean {
    return CONTENT_BLOCK_TYPES.includes(type);
  }

  isGroupType(type: string): boolean {
    return type === GROUP_TYPE;
  }

  isDateType(type: string): boolean {
    return type === 'date';
  }

  canPrefill(type: string): boolean {
    return this.PREFILLABLE_TYPES.includes(type);
  }

  isNoFutureDate(question: any): boolean {
    return question.dateMax === 'today';
  }

  toggleNoFutureDate(question: any, checked: boolean) {
    question.dateMax = checked ? 'today' : '';
  }

  // Backward-compat: rows saved before freeTextType existed only have the old
  // allowFreeText boolean — treat those as 'text' so they don't silently reset to None.
  private freeTextTypeFor(o: any): string {
    return o.freeTextType || (o.allowFreeText ? 'text' : '');
  }

  // AR-32: a stable id that survives a save (unlike AutoIndex, which doesn't -
  // saving a questionnaire deletes and re-inserts every question). Generated
  // client-side so a repeatableGroup's "count from" picker can reference a
  // number question the moment both exist on screen, with no round-trip to
  // the server needed first.
  private generateQuestionKey(): string {
    return 'q_' + Math.random().toString(36).slice(2, 10);
  }

  // Number questions available to a repeatableGroup's "count rows from"
  // picker: only ones that come BEFORE it in the list (matches the backend's
  // cross-reference validation in schema/questionnaires.js) - a group can't
  // size itself off an answer the patient hasn't given yet.
  numberQuestionsBefore(question: any): any[] {
    const index = this.editing.questions.indexOf(question);
    return this.editing.questions
      .slice(0, index)
      .filter((q: any) => q.questionType === 'number');
  }

  insertPlaceholder(question: any, token: string) {
    question.questionText = (question.questionText || '') + token;
  }

  // Display helper for the list view: exam names for a questionnaire's real
  // examIds plus any fallback name-only links (examNames).
  examLabel(q: any): string {
    const fromIds = (Array.isArray(q.examIds) ? q.examIds : [])
      .map((id) => {
        const exam = this.exams.find((e) => Number(e.AutoIndex) === Number(id));
        return exam ? (exam.examName || exam.examType) : '';
      });
    const fromNames = Array.isArray(q.examNames) ? q.examNames : [];
    return [...fromIds, ...fromNames].filter(Boolean).join(', ');
  }

  // ---- editor lifecycle ----

  newQuestionnaire() {
    this.editing = {
      AutoIndex: null,
      name: '',
      defaultLanguage: 'he',
      examSelection: [],
      questions: []
    };
  }

  editQuestionnaire(q: any) {
    // deep clone so edits are not reflected in the list until saved
    this.editing = {
      AutoIndex: q.AutoIndex,
      name: q.name,
      defaultLanguage: q.defaultLanguage || 'he',
      // combine real examId links and fallback name links into one selection (item 6)
      examSelection: [
        ...(Array.isArray(q.examIds) ? q.examIds.map((id) => String(id)) : []),
        ...(Array.isArray(q.examNames) ? q.examNames.map((n) => 'name:' + n) : [])
      ],
      questions: (q.questions || []).map((question) => this.cloneQuestion(question, question.AutoIndex))
    };
  }

  // Copy a company default into a fresh, unsaved questionnaire. Saving it creates
  // the doctor's own copy (AutoIndex null) without touching the default.
  useDefault(d: any) {
    this.editing = {
      AutoIndex: null,
      name: d.name,
      defaultLanguage: d.defaultLanguage || 'he',
      examSelection: [],
      questions: (d.questions || []).map((question) => this.cloneQuestion(question, null))
    };
  }

  // AR-32: shared by editQuestionnaire/useDefault so a repeatableGroup's
  // questionKey/repeatCountFromQuestionKey/minItems/subQuestions survive being
  // loaded back into the editor - a shallow field-by-field map here would
  // silently drop them (as it did before this existed), which on the next
  // save would delete the group's nested questions with no error at all.
  private cloneQuestion(question: any, autoIndex: any): any {
    const cloned: any = {
      AutoIndex: autoIndex,
      questionKey: question.questionKey || this.generateQuestionKey(),
      questionType: question.questionType,
      questionText: question.questionText,
      questionTextEng: question.questionTextEng || '',
      required: !!question.required,
      dateMin: question.dateMin || '',
      dateMax: question.dateMax || '',
      prefillField: question.prefillField || '',
      options: (question.options || []).map((o: any) => ({
        optionText: o.optionText, optionTextEng: o.optionTextEng || '', freeTextType: this.freeTextTypeFor(o)
      }))
    };
    if (this.isGroupType(question.questionType)) {
      cloned.repeatCountFromQuestionKey = question.repeatCountFromQuestionKey || '';
      cloned.minItems = question.minItems ?? 0;
      cloned.subQuestions = (question.subQuestions || []).map((sq: any) => ({
        questionKey: sq.questionKey || this.generateQuestionKey(),
        questionType: sq.questionType,
        questionText: sq.questionText,
        questionTextEng: sq.questionTextEng || '',
        required: !!sq.required,
        dateMin: sq.dateMin || '',
        dateMax: sq.dateMax || '',
        options: (sq.options || []).map((o: any) => ({
          optionText: o.optionText, optionTextEng: o.optionTextEng || '', freeTextType: this.freeTextTypeFor(o)
        }))
      }));
    }
    return cloned;
  }

  cancel() {
    this.editing = null;
    this.error = false;
  }

  // ---- question / option editing ----

  addQuestion() {
    this.editing.questions.push({
      AutoIndex: null,
      questionKey: this.generateQuestionKey(),
      questionType: 'text',
      questionText: '',
      questionTextEng: '',
      required: false,
      dateMin: '',
      dateMax: '',
      prefillField: '',
      options: []
    });
  }

  removeQuestion(index: number) {
    this.editing.questions.splice(index, 1);
  }

  onTypeChange(question: any) {
    // choice types need at least one option to start with; others carry none
    if (this.isChoiceType(question.questionType)) {
      if (!Array.isArray(question.options) || !question.options.length) {
        question.options = [{ optionText: '', optionTextEng: '', freeTextType: '' }];
      }
    } else {
      question.options = [];
    }
    if (!this.canPrefill(question.questionType)) question.prefillField = '';
    if (!this.isDateType(question.questionType)) { question.dateMin = ''; question.dateMax = ''; }

    // AR-32: switching into/out of a repeatable group.
    if (this.isGroupType(question.questionType)) {
      if (!Array.isArray(question.subQuestions)) question.subQuestions = [];
      if (question.repeatCountFromQuestionKey === undefined) question.repeatCountFromQuestionKey = '';
      if (question.minItems === undefined) question.minItems = 0;
    } else {
      delete question.subQuestions;
      delete question.repeatCountFromQuestionKey;
      delete question.minItems;
    }
  }

  addOption(question: any) {
    if (!Array.isArray(question.options)) question.options = [];
    question.options.push({ optionText: '', optionTextEng: '', freeTextType: '' });
  }

  removeOption(question: any, index: number) {
    question.options.splice(index, 1);
  }

  drop(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.editing.questions, event.previousIndex, event.currentIndex);
  }

  // ---- AR-32: repeatableGroup sub-question editing ----

  addSubQuestion(group: any) {
    if (!Array.isArray(group.subQuestions)) group.subQuestions = [];
    group.subQuestions.push({
      questionKey: this.generateQuestionKey(),
      questionType: 'text',
      questionText: '',
      questionTextEng: '',
      required: false,
      dateMin: '',
      dateMax: '',
      options: []
    });
  }

  removeSubQuestion(group: any, index: number) {
    group.subQuestions.splice(index, 1);
  }

  // Same rules as onTypeChange, minus prefillField (sub-questions can't prefill).
  onSubTypeChange(subQuestion: any) {
    if (this.isChoiceType(subQuestion.questionType)) {
      if (!Array.isArray(subQuestion.options) || !subQuestion.options.length) {
        subQuestion.options = [{ optionText: '', optionTextEng: '', freeTextType: '' }];
      }
    } else {
      subQuestion.options = [];
    }
    if (!this.isDateType(subQuestion.questionType)) { subQuestion.dateMin = ''; subQuestion.dateMax = ''; }
  }

  addSubOption(subQuestion: any) {
    if (!Array.isArray(subQuestion.options)) subQuestion.options = [];
    subQuestion.options.push({ optionText: '', optionTextEng: '', freeTextType: '' });
  }

  removeSubOption(subQuestion: any, index: number) {
    subQuestion.options.splice(index, 1);
  }

  dropSub(event: CdkDragDrop<any[]>, group: any) {
    moveItemInArray(group.subQuestions, event.previousIndex, event.currentIndex);
  }

  // ---- persistence ----

  // Split the combined exam selection back into real ids and fallback names (item 6).
  // replaceConflicts lets the backend take over exams already linked elsewhere (item 7).
  private buildPayload(replaceConflicts = false): any {
    const selection = Array.isArray(this.editing.examSelection) ? this.editing.examSelection : [];
    const examIds = selection.filter((k) => !String(k).startsWith('name:')).map((k) => Number(k));
    const examNames = selection.filter((k) => String(k).startsWith('name:')).map((k) => String(k).slice('name:'.length));
    const { examSelection, ...rest } = this.editing;
    return { ...rest, examIds, examNames, replaceConflicts };
  }

  async save(replaceConflicts = false) {
    if (!this.editing.name || !this.editing.name.trim()) {
      this.error = true;
      return;
    }
    try {
      const payload = this.buildPayload(replaceConflicts);
      let result;
      if (payload.AutoIndex == null) {
        result = await this.settingsService.saveQuestionnaire(payload);
      } else {
        result = await this.settingsService.updateQuestionnaire(payload);
      }
      if (result && result['status'] === 'ok') {
        this.error = false;
        this.closeConflictModal();
        this.editing = null;
        await this.loadQuestionnaires();
      } else {
        this.error = true;
      }
    } catch (error) {
      // The exam is already assigned to another questionnaire — let the doctor resolve it.
      if (error && error.status === 409 && error.error && error.error.conflicts) {
        this.examConflicts = error.error.conflicts;
        this.showConflictModal = true;
        return;
      }
      console.error('Error saving questionnaire:', error);
      this.error = true;
    }
  }

  // ---- exam-conflict resolution (item 7) ----

  // Stable selection key for a conflict row, matching examKey() / buildPayload().
  private conflictKey(c: any): string {
    return c && c.examId != null ? String(c.examId) : 'name:' + (c ? c.examName : '');
  }

  // "Save & replace": current questionnaire takes over all conflicting exams.
  replaceConflictsAndSave() {
    this.showConflictModal = false;
    this.save(true);
  }

  // "Remove these exams from this questionnaire": drop the conflicting exams from
  // the selection (leaving them on their current owner) and save.
  removeConflictsAndSave() {
    const conflictKeys = new Set(this.examConflicts.map((c) => this.conflictKey(c)));
    this.editing.examSelection = (this.editing.examSelection || []).filter((k) => !conflictKeys.has(String(k)));
    this.closeConflictModal();
    this.save();
  }

  closeConflictModal() {
    this.showConflictModal = false;
    this.examConflicts = [];
  }

  async deleteQuestionnaire(q: any) {
    // Deletion is permanent (the questionnaire and all its questions/options are
    // removed) — patients' already-submitted answers are unaffected, since those
    // are stored as an independent snapshot.
    if (!confirm(`Permanently delete "${q.name}"? This cannot be undone.`)) return;
    try {
      const result = await this.settingsService.deleteQuestionnaire(q.AutoIndex);
      if (result && result['status'] === 'ok') {
        this.error = false;
        await this.loadQuestionnaires();
      }
    } catch (error) {
      console.error('Error deleting questionnaire:', error);
      this.error = true;
    }
  }

  // Used by the settings host to guard navigation with unsaved edits.
  checkIfEditing(): boolean {
    return !!this.editing;
  }
}
